import { useModuleNavigation } from '../modules/registry';
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { loginSuccess } from "../slices/auth/login/reducer";
import { getLoggedinUser } from "../helpers/api_helper";
import useTierAccess from "../helpers/useTierAccess";

const Navdata = ({ showAllMenus, toggleAllMenus }) => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.Login);
  
  // Force re-render when user changes
  const [renderKey, setRenderKey] = useState(0);
  
  // One-time initialization on component mount
  useEffect(() => {
    const sessionUser = getLoggedinUser();
    
    if (sessionUser && sessionUser.email) {
      dispatch(loginSuccess(sessionUser));
    }
  }, []); // Empty dependency array for one-time execution
  
  // Initialize Redux state from sessionStorage if user is logged in but Redux doesn't have user data
  useEffect(() => {
    const sessionUser = getLoggedinUser();
    
    if (sessionUser && sessionUser.email) {
      if (!user || !user.email || user.email !== sessionUser.email) {
        dispatch(loginSuccess(sessionUser));
      }
    }
  }, [dispatch]); // Remove user from dependency to avoid infinite loop
  
  // Force re-render when user data changes
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [user?.email, user?.role, user?.username]);
  
  // Listen for auth state changes
  useEffect(() => {
    const handleAuthUpdate = () => {
      const sessionUser = getLoggedinUser();
      if (sessionUser && sessionUser.email) {
        dispatch(loginSuccess(sessionUser));
        setRenderKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('authUserUpdated', handleAuthUpdate);
    
    return () => {
      window.removeEventListener('authUserUpdated', handleAuthUpdate);
    };
  }, [dispatch]);
  
  // Check if user is super admin (memoized to force re-render)
  const isAdmin = useMemo(() => {
    // Try Redux first, then fall back to session storage
    const reduxUser = user;
    const sessionUser = getLoggedinUser();
    
    // Use Redux user if available, otherwise use session user
    const effectiveUser = reduxUser?.email ? reduxUser : sessionUser;
    
    // users.is_admin is the source of truth (returned by login and /api/user/profile).
    // The legacy checks stay as a fallback for sessions issued before that column existed.
    const adminStatus = effectiveUser?.is_admin === true ||
                       effectiveUser?.is_admin === 1 ||
                       effectiveUser?.email === 'houzhip@gmail.com' || 
                       effectiveUser?.role === 'admin' || 
                       effectiveUser?.username === 'admin';
    
    return adminStatus;
  }, [user?.email, user?.role, user?.username, user?.is_admin, renderKey]); // Add renderKey to force recalculation
  
  // Reports render system-generated forecasts, which the API gates per symbol —
  // offering those menus to a free member would only lead to empty pages.
  const tierAccess = useTierAccess();
  const canSeeSystemResearch = tierAccess.loading ? null : tierAccess.canSeeSystemResearch;
  const pluginMenu = useModuleNavigation({ admin: isAdmin || tierAccess.admin, paid: Number(tierAccess.tier) > 0 });

  //state data
  const [isDashboard, setIsDashboard] = useState(false);
  const [isApps, setIsApps] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [isPages, setIsPages] = useState(false);
  const [isBaseUi, setIsBaseUi] = useState(false);
  const [isAdvanceUi, setIsAdvanceUi] = useState(false);
  const [isForms, setIsForms] = useState(false);
  const [isTables, setIsTables] = useState(false);
  const [isCharts, setIsCharts] = useState(false);
  const [isIcons, setIsIcons] = useState(false);
  const [isMaps, setIsMaps] = useState(false);
  const [isMultiLevel, setIsMultiLevel] = useState(false);

  // Prediction Tracker
  const [isPredictions, setIsPredictions] = useState(false);

  // My Research (bring-your-own-AI)
  const [isMyResearch, setIsMyResearch] = useState(false);

  // Report Menu Group
  const [isReport, setIsReport] = useState(false);
  
  // Previous Report Menu Group
  const [isPreviousReport, setIsPreviousReport] = useState(false);

  // Prediction History (Mode 2)
  const [isPredictionHistory, setIsPredictionHistory] = useState(false);

  // Score Report (Mode 3)
  const [isScoreReport, setIsScoreReport] = useState(false);

  // System Admin (Super User Only)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  //Calender
  const [isCalender, setCalender] = useState(false);

  // Apps
  const [isEmail, setEmail] = useState(false);
  const [isSubEmail, setSubEmail] = useState(false);
  const [isEcommerce, setIsEcommerce] = useState(false);
  const [isProjects, setIsProjects] = useState(false);
  const [isTasks, setIsTasks] = useState(false);
  const [isCRM, setIsCRM] = useState(false);
  const [isCrypto, setIsCrypto] = useState(false);
  const [isInvoices, setIsInvoices] = useState(false);
  const [isSupportTickets, setIsSupportTickets] = useState(false);
  const [isNFTMarketplace, setIsNFTMarketplace] = useState(false);
  const [isJobs, setIsJobs] = useState(false);
  const [isJobList, setIsJobList] = useState(false);
  const [isCandidateList, setIsCandidateList] = useState(false);

  // Authentication
  const [isSignIn, setIsSignIn] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isPasswordCreate, setIsPasswordCreate] = useState(false);
  const [isLockScreen, setIsLockScreen] = useState(false);
  const [isLogout, setIsLogout] = useState(false);
  const [isSuccessMessage, setIsSuccessMessage] = useState(false);
  const [isVerification, setIsVerification] = useState(false);
  const [isError, setIsError] = useState(false);

  // Pages
  const [isProfile, setIsProfile] = useState(false);
  const [isLanding, setIsLanding] = useState(false);
  const [isBlog, setIsBlog] = useState(false);

  // Charts
  const [isApex, setIsApex] = useState(false);

  // Multi Level
  const [isLevel1, setIsLevel1] = useState(false);
  const [isLevel2, setIsLevel2] = useState(false);

  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  function updateIconSidebar(e) {
    if (e && e.target && e.target.getAttribute("subitems")) {
      const ul = document.getElementById("two-column-menu");
      const iconItems = ul.querySelectorAll(".nav-icon.active");
      let activeIconItems = [...iconItems];
      activeIconItems.forEach((item) => {
        item.classList.remove("active");
        var id = item.getAttribute("subitems");
        if (document.getElementById(id))
          document.getElementById(id).classList.remove("show");
      });
    }
  }

  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");
    if (iscurrentState !== "Dashboard") {
      setIsDashboard(false);
    }
    if (iscurrentState !== "Apps") {
      setIsApps(false);
    }
    if (iscurrentState !== "Auth") {
      setIsAuth(false);
    }
    if (iscurrentState !== "Pages") {
      setIsPages(false);
    }
    if (iscurrentState !== "BaseUi") {
      setIsBaseUi(false);
    }
    if (iscurrentState !== "AdvanceUi") {
      setIsAdvanceUi(false);
    }
    if (iscurrentState !== "Forms") {
      setIsForms(false);
    }
    if (iscurrentState !== "Tables") {
      setIsTables(false);
    }
    if (iscurrentState !== "Charts") {
      setIsCharts(false);
    }
    if (iscurrentState !== "Icons") {
      setIsIcons(false);
    }
    if (iscurrentState !== "Maps") {
      setIsMaps(false);
    }
    if (iscurrentState !== "MuliLevel") {
      setIsMultiLevel(false);
    }
    if (iscurrentState !== "Predictions") {
      setIsPredictions(false);
    }
    if (iscurrentState !== "MyResearch") {
      setIsMyResearch(false);
    }
    if (iscurrentState !== "PredictionHistory") {
      setIsPredictionHistory(false);
    }
    if (iscurrentState !== "ScoreReport") {
      setIsScoreReport(false);
    }
    if (iscurrentState !== "SystemAdmin") {
      setIsSystemAdmin(false);
    }
    if (iscurrentState === "Widgets") {
      history("/widgets");
      document.body.classList.add("twocolumn-panel");
    }
    if (iscurrentState !== "Landing") {
      setIsLanding(false);
    }
  }, [
    history,
    iscurrentState,
    isDashboard,
    isApps,
    isAuth,
    isPages,
    isBaseUi,
    isAdvanceUi,
    isForms,
    isTables,
    isCharts,
    isIcons,
    isMaps,
    isMultiLevel,
    isPredictions,
    isSystemAdmin,
    showAllMenus,
  ]);

  // Compute today/yesterday strings once, accounting for EST timezone
  const now = new Date();
  
  // Convert UTC to EST (UTC-5 in standard time, UTC-4 in daylight time)
  // For simplicity, using UTC-4 (EDT) as it's currently daylight saving time
  const estDate = new Date(now.getTime() - (4 * 60 * 60 * 1000));
  
  const yyyy = estDate.getFullYear();
  const mm = String(estDate.getMonth() + 1).padStart(2, '0');
  const dd = String(estDate.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;
  
  const yest = new Date(estDate.getTime() - 24 * 60 * 60 * 1000);
  const yyyyy = yest.getFullYear();
  const ymm = String(yest.getMonth() + 1).padStart(2, '0');
  const ydd = String(yest.getDate()).padStart(2, '0');
  const yesterdayStr = `${yyyyy}-${ymm}-${ydd}`;

  // Tomorrow's date string
  const tmrw = new Date(estDate.getTime() + 24 * 60 * 60 * 1000);
  const tmrrw_yyyy = tmrw.getFullYear();
  const tmrrw_mm = String(tmrw.getMonth() + 1).padStart(2, '0');
  const tmrrw_dd = String(tmrw.getDate()).padStart(2, '0');
  const tomorrowStr = `${tmrrw_yyyy}-${tmrrw_mm}-${tmrrw_dd}`;

  // Last 30 days range (EST-based)
  const last30StartDate = new Date(estDate);
  last30StartDate.setDate(last30StartDate.getDate() - 30);
  const last30yyyy = last30StartDate.getFullYear();
  const last30mm = String(last30StartDate.getMonth() + 1).padStart(2, '0');
  const last30dd = String(last30StartDate.getDate()).padStart(2, '0');
  const last30StartStr = `${last30yyyy}-${last30mm}-${last30dd}`;

  // Primary models for daily reports - extended set with premium variants
  const primaryModels = 'BIS+gpt+Claude-premium+gpt-premium+Gemini-premium+Claude+Gemini';
  const primaryModelsParam = encodeURIComponent(primaryModels);

  // Basic models for 4h reports - extended set with premium variants
  // Include internal bis-v1 in the lightweight 4h model set so menu links include BIS alongside other fast/cheap models
  const basicModels = 'BIS+gpt+Claude-premium+gpt-premium+Gemini-premium+Claude+Gemini';
  const basicModelsParam = encodeURIComponent(basicModels);

  // Track which date to use per symbol for daily (today if available else yesterday)
  const [dailyDateMap, setDailyDateMap] = useState({ BTC: todayStr, ETH: todayStr, PAXG: todayStr, XRP: todayStr, AVAX: todayStr, ADA: todayStr, ARB: todayStr, LINK: todayStr, UNI: todayStr, DOGE: todayStr, SPY: todayStr, TQQQ: todayStr, UGL: todayStr, IBIT: todayStr, SOXL: todayStr, FAS: todayStr, TMF: todayStr, LABU: todayStr, UCO: todayStr });

  // On mount, check availability of today's daily predictions; if none, fallback to yesterday
  useEffect(() => {
    let cancelled = false;
    const symbols = ['BTC', 'ETH', 'PAXG', 'XRP', 'SPY'];
    const checkSymbol = async (symbol) => {
      try {
        // First try today's data (EST date)
        let params = new URLSearchParams({ symbol, from: todayStr, to: todayStr, timeframe: '1d' });
        let res = await fetch(`/api/report-data?${params.toString()}`);
        let json = await res.json();
        let hasToday = Array.isArray(json?.data) && json.data.length > 0;
        
        if (hasToday) {
          return { symbol, date: todayStr };
        }
        
        // If no today data, check if it's after 5 PM EST
        const estHour = estDate.getUTCHours();
        
        // If it's after 5 PM EST and we don't have today's data, still try today first
        // as the data might be available but not yet indexed, or there might be a delay
        if (estHour >= 17) {
          // After 5 PM EST, prefer today even if no data found yet
          return { symbol, date: todayStr };
        } else {
          // Before 5 PM EST, use yesterday's data
          return { symbol, date: yesterdayStr };
        }
      } catch (e) {
        // On error, default based on time - if after 5 PM EST use today, else yesterday
        const estHour = estDate.getUTCHours();
        return { symbol, date: estHour >= 17 ? todayStr : yesterdayStr };
      }
    };
    (async () => {
      const results = await Promise.all(symbols.map(checkSymbol));
      if (cancelled) return;
      const next = { ...dailyDateMap };
      results.forEach(r => { next[r.symbol] = r.date; });
      setDailyDateMap(next);
    })();
    return () => { cancelled = true; };
  }, []);  const menuItems = useMemo(() => {
  // Helper: build daily report link with 3-day range (yesterday to tomorrow)
  const makeDailyReportLink = (symbol) => {
    return `/report?symbol=${symbol}&from=${yesterdayStr}&to=${tomorrowStr}&timeframe=1d&model=${primaryModelsParam}&performance=true`;
  };
  
  // Helper: build 4h report link (UTC date, hours points to latest 4h slot start hour by UTC)
  const make4hReportLink = (symbol) => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const utcHour = now.getUTCHours();
    const slotStartHour = Math.floor(utcHour / 4) * 4; // 0,4,8,12,16,20
    const hoursParam = `${slotStartHour}h`;
    return `/report?mode=1&symbol=${symbol}&from=${dateStr}&to=${dateStr}&timeframe=4h&model=${basicModelsParam}&hours=${hoursParam}&hidepanel=true&performance=true`;
  };

  // Helper: build 1h report link (UTC date, hours points to current UTC hour)
  const make1hReportLink = (symbol) => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const utcHour = now.getUTCHours();
    const hoursParam = `${utcHour}h`;
    return `/report?mode=1&symbol=${symbol}&from=${dateStr}&to=${dateStr}&timeframe=1h&model=${basicModelsParam}&hours=${hoursParam}&hidepanel=true&performance=true`;
  };
  
  const todaysBtcReportLink = makeDailyReportLink('BTC');
  const todaysEthReportLink = makeDailyReportLink('ETH');
  const todaysPaxgReportLink = makeDailyReportLink('PAXG');
  const todaysXrpReportLink = makeDailyReportLink('XRP');
  const todaysSpyReportLink = makeDailyReportLink('SPY');
  const todaysTqqqReportLink = makeDailyReportLink('TQQQ');
  const todaysUglReportLink = makeDailyReportLink('UGL');
  const todaysSoxl1dReportLink = makeDailyReportLink('SOXL');
  const todaysFas1dReportLink = makeDailyReportLink('FAS');
  const todaysTmf1dReportLink = makeDailyReportLink('TMF');
  const todaysLabu1dReportLink = makeDailyReportLink('LABU');
  const todaysUco1dReportLink = makeDailyReportLink('UCO');
  const todaysFts1dReportLink = makeDailyReportLink('FTS');
  const todaysIbit1dReportLink = makeDailyReportLink('IBIT');
  const todaysIonq1dReportLink = makeDailyReportLink('IONQ');
  const todaysCeg1dReportLink = makeDailyReportLink('CEG');
  const todaysQbts1dReportLink = makeDailyReportLink('QBTS');
  const todaysMp1dReportLink = makeDailyReportLink('MP');
  const todaysGoog1dReportLink = makeDailyReportLink('GOOG');
  const todaysTsla1dReportLink = makeDailyReportLink('TSLA');
  const todaysAmzn1dReportLink = makeDailyReportLink('AMZN');
  const todaysAapl1dReportLink = makeDailyReportLink('AAPL');
  const todaysMsft1dReportLink = makeDailyReportLink('MSFT');
  const todaysNvda1dReportLink = makeDailyReportLink('NVDA');
  const todaysMeta1dReportLink = makeDailyReportLink('META');
  const todaysWsp1dReportLink = makeDailyReportLink('WSP.TO');
  // Mode 2 and 3 helper links (last 30 days range), 1d and 4h variants
  const makeMode2DailyLink = (symbol) => {
  // Include primary models by default to mirror Mode 1 daily behavior
  return `/report?mode=2&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=1d&model=${primaryModelsParam}&hidepanel=true&performance=true`;
  };
  const makeMode2_4hLink = (symbol) => {
  // Include basic models by default for 4h
  return `/report?mode=2&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=4h&model=${basicModelsParam}&hidepanel=true&performance=true`;
  };
  const makeMode2_1hLink = (symbol) => {
  return `/report?mode=2&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=1h&model=${basicModelsParam}&hidepanel=true&performance=true`;
  };
  const makeMode3DailyLink = (symbol) => {
  // Include primary models by default to mirror Mode 1 daily behavior
  return `/report?mode=3&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=1d&model=${primaryModelsParam}&hidepanel=true&performance=true`;
  };
  const makeMode3_4hLink = (symbol) => {
  // Include basic models by default for 4h
  return `/report?mode=3&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=4h&model=${basicModelsParam}&hidepanel=true&performance=true`;
  };
  const makeMode3_1hLink = (symbol) => {
  return `/report?mode=3&symbol=${symbol}&from=${last30StartStr}&to=${todayStr}&timeframe=1h&model=${basicModelsParam}&hidepanel=true&performance=true`;
  };

  // Helper: build previous daily report link (1 day before the current report date)
  // Use UTC-based arithmetic to avoid local timezone off-by-one issues (e.g., date-only strings parsed as UTC)
  const makePreviousDailyReportLink = (symbol) => {
    const currentDate = dailyDateMap[symbol] || todayStr; // 'YYYY-MM-DD'
    const [yy, mm2, dd2] = (currentDate || '').split('-').map((v) => parseInt(v, 10));
    // Fallback: if parsing fails, default to todayStr split
    const y = Number.isFinite(yy) ? yy : parseInt(todayStr.slice(0, 4), 10);
    const m = Number.isFinite(mm2) ? mm2 : parseInt(todayStr.slice(5, 7), 10);
    const d = Number.isFinite(dd2) ? dd2 : parseInt(todayStr.slice(8, 10), 10);
    const dt = new Date(Date.UTC(y, (m - 1), d));
    dt.setUTCDate(dt.getUTCDate() - 1);
    const year = dt.getUTCFullYear();
    const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dt.getUTCDate()).padStart(2, '0');
    const previousDateStr = `${year}-${month}-${day}`;
    return `/report?mode=1&symbol=${symbol}&from=${previousDateStr}&to=${previousDateStr}&timeframe=1d&model=${primaryModelsParam}&hidepanel=true&previous=true&performance=true`;
  };
  
  // Helper: build previous 4h report link (4 hours before current 4h slot)
  const makePrevious4hReportLink = (symbol) => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const utcHour = now.getUTCHours();
    const currentSlotStartHour = Math.floor(utcHour / 4) * 4; // 0,4,8,12,16,20
    
    // Calculate previous 4h slot
    let previousSlotStartHour = currentSlotStartHour - 4;
    let dateStr = `${year}-${month}-${day}`;
    
    // If previous slot goes to previous day
    if (previousSlotStartHour < 0) {
      previousSlotStartHour = 20; // Last slot of previous day
      const prevDay = new Date(now);
      prevDay.setUTCDate(prevDay.getUTCDate() - 1);
      const prevYear = prevDay.getUTCFullYear();
      const prevMonth = String(prevDay.getUTCMonth() + 1).padStart(2, '0');
      const prevDayStr = String(prevDay.getUTCDate()).padStart(2, '0');
      dateStr = `${prevYear}-${prevMonth}-${prevDayStr}`;
    }
    
    const hoursParam = `${previousSlotStartHour}h`;
    return `/report?mode=1&symbol=${symbol}&from=${dateStr}&to=${dateStr}&timeframe=4h&model=${basicModelsParam}&hours=${hoursParam}&hidepanel=true&previous=true&performance=true`;
  };

  // Helper: build previous 1h report link (1 hour before current UTC hour)
  const makePrevious1hReportLink = (symbol) => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const utcHour = now.getUTCHours();
    let previousHour = utcHour - 1;
    let dateStr = `${year}-${month}-${day}`;
    if (previousHour < 0) {
      previousHour = 23;
      const prevDay = new Date(now);
      prevDay.setUTCDate(prevDay.getUTCDate() - 1);
      const prevYear = prevDay.getUTCFullYear();
      const prevMonth = String(prevDay.getUTCMonth() + 1).padStart(2, '0');
      const prevDayStr = String(prevDay.getUTCDate()).padStart(2, '0');
      dateStr = `${prevYear}-${prevMonth}-${prevDayStr}`;
    }
    const hoursParam = `${previousHour}h`;
    return `/report?mode=1&symbol=${symbol}&from=${dateStr}&to=${dateStr}&timeframe=1h&model=${basicModelsParam}&hours=${hoursParam}&hidepanel=true&previous=true&performance=true`;
  };
  
  const previousBtcReportLink = makePreviousDailyReportLink('BTC');
  const previousEthReportLink = makePreviousDailyReportLink('ETH');
  const previousPaxgReportLink = makePreviousDailyReportLink('PAXG');
  const previousXrpReportLink = makePreviousDailyReportLink('XRP');
  const previousSpyReportLink = makePreviousDailyReportLink('SPY');
  const previousTqqqReportLink = makePreviousDailyReportLink('TQQQ');
  const previousUglReportLink = makePreviousDailyReportLink('UGL');
  const previousSoxl1dReportLink = makePreviousDailyReportLink('SOXL');
  const previousFas1dReportLink = makePreviousDailyReportLink('FAS');
  const previousTmf1dReportLink = makePreviousDailyReportLink('TMF');
  const previousLabu1dReportLink = makePreviousDailyReportLink('LABU');
  const previousUco1dReportLink = makePreviousDailyReportLink('UCO');
  const previousFts1dReportLink = makePreviousDailyReportLink('FTS');
  const previousIbit1dReportLink = makePreviousDailyReportLink('IBIT');
  const previousIonq1dReportLink = makePreviousDailyReportLink('IONQ');
  const previousCeg1dReportLink = makePreviousDailyReportLink('CEG');
  const previousQbts1dReportLink = makePreviousDailyReportLink('QBTS');
  const previousMp1dReportLink = makePreviousDailyReportLink('MP');
  const previousGoog1dReportLink = makePreviousDailyReportLink('GOOG');
  const previousTsla1dReportLink = makePreviousDailyReportLink('TSLA');
  const previousAmzn1dReportLink = makePreviousDailyReportLink('AMZN');
  const previousAapl1dReportLink = makePreviousDailyReportLink('AAPL');
  const previousMsft1dReportLink = makePreviousDailyReportLink('MSFT');
  const previousNvda1dReportLink = makePreviousDailyReportLink('NVDA');
  const previousMeta1dReportLink = makePreviousDailyReportLink('META');
  const previousWsp1dReportLink = makePreviousDailyReportLink('WSP.TO');

  const toggleReportPanel = () => {
    const { pathname, search } = window.location;
    
    if (pathname.startsWith('/report')) {
      // Delegate URL/state updates to the Report page to preserve all params.
      // Pass the current search so the page can use it even if the menu click alters the URL.
      window.dispatchEvent(new CustomEvent('report:toggle-panel', { detail: { search } }));
      return;
    }
    // If not on report page, navigate to default daily with primary models and panel hidden (today or fallback)
    const date = dailyDateMap['BTC'] || todayStr;
    history(`/report?mode=1&symbol=BTC&from=${date}&to=${date}&timeframe=1d&model=${primaryModelsParam}&hidepanel=true`);
  };

    const allMenuItems = [
    {
      label: "Menu",
      isHeader: true,
    },
    {
      id: "dashboard",
      label: "Dashboards",
      icon: "ri-dashboard-2-line",
      link: "/#",
      stateVariables: isDashboard,
      click: function (e) {
        e.preventDefault();
        setIsDashboard(!isDashboard);
        setIscurrentState("Dashboard");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "analytics",
          label: "Analytics",
          link: "/dashboard-analytics",
          parentId: "dashboard",
        },
        {
          id: "crm",
          label: "CRM",
          link: "/dashboard-crm",
          parentId: "dashboard",
        },
        {
          id: "ecommerce",
          label: "Ecommerce",
          link: "/dashboard",
          parentId: "dashboard",
        },
        {
          id: "crypto",
          label: "Crypto",
          link: "/dashboard-crypto",
          parentId: "dashboard",
        },
        {
          id: "projects",
          label: "Projects",
          link: "/dashboard-projects",
          parentId: "dashboard",
        },
        {
          id: "nft",
          label: "NFT",
          link: "/dashboard-nft",
          parentId: "dashboard",
        },
        {
          id: "job",
          label: "Job",
          link: "/dashboard-job",
          parentId: "dashboard",
        },
        {
          id: "blog",
          label: "Blog",
          link: "/dashboard-blog",
          parentId: "dashboard",
          badgeColor: "success",
          badgeName: "New",
        },
      ],
    },
    {
      id: "apps",
      label: "Apps",
      icon: "ri-apps-2-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsApps(!isApps);
        setIscurrentState("Apps");
        updateIconSidebar(e);
      },
      stateVariables: isApps,
      subItems: [
        {
          id: "calendar",
          label: "Calendar",
          link: "/#",
          parentId: "apps",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setCalender(!isCalender);
          },
          stateVariables: isCalender,
          childItems: [
            {
              id: 1,
              label: "Main Calender",
              link: "/apps-calendar",
              parentId: "apps",
            },
            {
              id: 2,
              label: "Month Grid",
              link: "/apps-calendar-month-grid",
              parentId: "apps",
            },
          ],
        },
        {
          id: "chat",
          label: "Chat",
          link: "/apps-chat",
          parentId: "apps",
        },
        {
          id: "mailbox",
          label: "Email",
          link: "/#",
          parentId: "apps",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setEmail(!isEmail);
          },
          stateVariables: isEmail,
          childItems: [
            {
              id: 1,
              label: "Mailbox",
              link: "/apps-mailbox",
              parentId: "apps",
            },
            {
              id: 2,
              label: "Email Templates",
              link: "/#",
              parentId: "apps",
              isChildItem: true,
              stateVariables: isSubEmail,
              click: function (e) {
                e.preventDefault();
                setSubEmail(!isSubEmail);
              },
              childItems: [
                {
                  id: 2,
                  label: "Basic Action",
                  link: "/apps-email-basic",
                  parentId: "apps",
                },
                {
                  id: 3,
                  label: "Ecommerce Action",
                  link: "/apps-email-ecommerce",
                  parentId: "apps",
                },
              ],
            },
          ],
        },
        {
          id: "appsecommerce",
          label: "Ecommerce",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsEcommerce(!isEcommerce);
          },
          parentId: "apps",
          stateVariables: isEcommerce,
          childItems: [
            {
              id: 1,
              label: "Products",
              link: "/apps-ecommerce-products",
              parentId: "apps",
            },
            {
              id: 2,
              label: "Product Details",
              link: "/apps-ecommerce-product-details",
              parentId: "apps",
            },
            {
              id: 3,
              label: "Create Product",
              link: "/apps-ecommerce-add-product",
              parentId: "apps",
            },
            {
              id: 4,
              label: "Orders",
              link: "/apps-ecommerce-orders",
              parentId: "apps",
            },
            {
              id: 5,
              label: "Order Details",
              link: "/apps-ecommerce-order-details",
              parentId: "apps",
            },
            {
              id: 6,
              label: "Customers",
              link: "/apps-ecommerce-customers",
              parentId: "apps",
            },
            {
              id: 7,
              label: "Shopping Cart",
              link: "/apps-ecommerce-cart",
              parentId: "apps",
            },
            {
              id: 8,
              label: "Checkout",
              link: "/apps-ecommerce-checkout",
              parentId: "apps",
            },
            {
              id: 9,
              label: "Sellers",
              link: "/apps-ecommerce-sellers",
              parentId: "apps",
            },
            {
              id: 10,
              label: "Seller Details",
              link: "/apps-ecommerce-seller-details",
              parentId: "apps",
            },
          ],
        },
        {
          id: "appsprojects",
          label: "Projects",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsProjects(!isProjects);
          },
          parentId: "apps",
          stateVariables: isProjects,
          childItems: [
            {
              id: 1,
              label: "List",
              link: "/apps-projects-list",
              parentId: "apps",
            },
            {
              id: 2,
              label: "Overview",
              link: "/apps-projects-overview",
              parentId: "apps",
            },
            {
              id: 3,
              label: "Create Project",
              link: "/apps-projects-create",
              parentId: "apps",
            },
          ],
        },
        {
          id: "tasks",
          label: "Tasks",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsTasks(!isTasks);
          },
          parentId: "apps",
          stateVariables: isTasks,
          childItems: [
            { id: 1, label: "Kanban Board", link: "/apps-tasks-kanban", parentId: "apps", },
            {
              id: 2,
              label: "List View",
              link: "/apps-tasks-list-view",
              parentId: "apps",
            },
            {
              id: 3,
              label: "Task Details",
              link: "/apps-tasks-details",
              parentId: "apps",
            },
          ],
        },
        {
          id: "appscrm",
          label: "CRM",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsCRM(!isCRM);
          },
          parentId: "apps",
          stateVariables: isCRM,
          childItems: [
            { id: 1, label: "Contacts", link: "/apps-crm-contacts" },
            { id: 2, label: "Companies", link: "/apps-crm-companies" },
            { id: 3, label: "Deals", link: "/apps-crm-deals" },
            { id: 4, label: "Leads", link: "/apps-crm-leads" },
          ],
        },
        {
          id: "appscrypto",
          label: "Crypto",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsCrypto(!isCrypto);
          },
          parentId: "apps",
          stateVariables: isCrypto,
          childItems: [
            { id: 1, label: "Transactions", link: "/apps-crypto-transactions" },
            { id: 2, label: "Buy & Sell", link: "/apps-crypto-buy-sell" },
            { id: 3, label: "Orders", link: "/apps-crypto-orders" },
            { id: 4, label: "My Wallet", link: "/apps-crypto-wallet" },
            { id: 5, label: "ICO List", link: "/apps-crypto-ico" },
            { id: 6, label: "KYC Application", link: "/apps-crypto-kyc" },
          ],
        },
        {
          id: "invoices",
          label: "Invoices",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsInvoices(!isInvoices);
          },
          parentId: "apps",
          stateVariables: isInvoices,
          childItems: [
            { id: 1, label: "List View", link: "/apps-invoices-list" },
            { id: 2, label: "Details", link: "/apps-invoices-details" },
            { id: 3, label: "Create Invoice", link: "/apps-invoices-create" },
          ],
        },
        {
          id: "supportTickets",
          label: "Support Tickets",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsSupportTickets(!isSupportTickets);
          },
          parentId: "apps",
          stateVariables: isSupportTickets,
          childItems: [
            { id: 1, label: "List View", link: "/apps-tickets-list" },
            { id: 2, label: "Ticket Details", link: "/apps-tickets-details" },
          ],
        },
        {
          id: "NFTMarketplace",
          label: "NFT Marketplace",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsNFTMarketplace(!isNFTMarketplace);
          },
          parentId: "apps",
          stateVariables: isNFTMarketplace,
          childItems: [
            { id: 1, label: "Marketplace", link: "/apps-nft-marketplace" },
            { id: 2, label: "Explore Now", link: "/apps-nft-explore" },
            { id: 3, label: "Live Auction", link: "/apps-nft-auction" },
            { id: 4, label: "Item Details", link: "/apps-nft-item-details" },
            { id: 5, label: "Collections", link: "/apps-nft-collections" },
            { id: 6, label: "Creators", link: "/apps-nft-creators" },
            { id: 7, label: "Ranking", link: "/apps-nft-ranking" },
            { id: 8, label: "Wallet Connect", link: "/apps-nft-wallet" },
            { id: 9, label: "Create NFT", link: "/apps-nft-create" },
          ],
        },
        {
          id: "filemanager",
          label: "File Manager",
          link: "/apps-file-manager",
          parentId: "apps",
        },
        {
          id: "todo",
          label: "To Do",
          link: "/apps-todo",
          parentId: "apps",
        },
        {
          id: "job",
          label: "Jobs",
          link: "/#",
          parentId: "apps",
          // badgeName: "New",
          // badgeColor: "success",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsJobs(!isJobs);
          },
          stateVariables: isJobs,
          childItems: [
            {
              id: 1,
              label: "Statistics",
              link: "/apps-job-statistics",
              parentId: "apps",
            },
            {
              id: 2,
              label: "Job Lists",
              link: "/#",
              parentId: "apps",
              isChildItem: true,
              stateVariables: isJobList,
              click: function (e) {
                e.preventDefault();
                setIsJobList(!isJobList);
              },
              childItems: [
                {
                  id: 1,
                  label: "List",
                  link: "/apps-job-lists",
                  parentId: "apps",
                },
                {
                  id: 2,
                  label: "Grid",
                  link: "/apps-job-grid-lists",
                  parentId: "apps",
                },
                {
                  id: 3,
                  label: "Overview",
                  link: "/apps-job-details",
                  parentId: "apps",
                },
              ],
            },
            {
              id: 3,
              label: "Candidate Lists",
              link: "/#",
              parentId: "apps",
              isChildItem: true,
              stateVariables: isCandidateList,
              click: function (e) {
                e.preventDefault();
                setIsCandidateList(!isCandidateList);
              },
              childItems: [
                {
                  id: 1,
                  label: "List View",
                  link: "/apps-job-candidate-lists",
                  parentId: "apps",
                },
                {
                  id: 2,
                  label: "Grid View",
                  link: "/apps-job-candidate-grid",
                  parentId: "apps",
                },
              ],
            },
            {
              id: 4,
              label: "Application",
              link: "/apps-job-application",
              parentId: "apps",
            },
            {
              id: 5,
              label: "New Job",
              link: "/apps-job-new",
              parentId: "apps",
            },
            {
              id: 6,
              label: "Companies List",
              link: "/apps-job-companies-lists",
              parentId: "apps",
            },
            {
              id: 7,
              label: "Job Categories",
              link: "/apps-job-categories",
              parentId: "apps",
            },
          ],
        },
        {
          id: "apikey",
          label: "API Key",
          link: "/apps-api-key",
          parentId: "apps",
          // badgeName: "New",
          // badgeColor: "success"
        },
      ],
    },
    // System Admin Menu (Super User Only) - Direct approach
    {
      id: "system-admin",
      label: "System Admin",
      icon: "ri-shield-user-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsSystemAdmin(!isSystemAdmin);
        setIscurrentState("SystemAdmin");
        updateIconSidebar(e);
      },
      stateVariables: isSystemAdmin,
      subItems: [
        {
          id: "settlement-center", label: "Settlement Center", link: "/admin-settlement", parentId: "system-admin",
        },
        {
          id: "access-requests",
          label: "Access Requests",
          link: "/access-requests",
          parentId: "system-admin",
        },
        {
          id: "user-activity",
          label: "User Activity",
          link: "/user-activity",
          parentId: "system-admin",
        },
        {
          id: "data-quality",
          label: "Data Quality",
          link: "/data-quality",
          parentId: "system-admin",
        },
        {
          id: "recent-jobs",
          label: "Recent Jobs",
          link: "/recent-jobs",
          parentId: "system-admin",
        },
        {
          id: "job-management",
          label: "Job Management",
          link: "/job-management",
          parentId: "system-admin",
        },
        {
          id: "system-logs",
          label: "System Logs",
          link: "/system-logs",
          parentId: "system-admin",
        },
        {
          id: "user-management",
          label: "User Management",
          link: "/user-management",
          parentId: "system-admin",
        },
        {
          id: "toggle-all-menus",
          label: showAllMenus ? "Hide All Menus" : "Show All Menus",
          parentId: "system-admin",
          isChildItem: true,
          click: toggleAllMenus,
        },
      ],
      isVisible: isAdmin,  // Show only for admin users
    },
    {
      id: "my-research",
      label: "My Research",
      icon: "ri-robot-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsMyResearch(!isMyResearch);
        setIscurrentState("MyResearch");
        updateIconSidebar(e);
      },
      stateVariables: isMyResearch,
      subItems: [
        {
          id: "my-research-generate",
          label: "Generate Research",
          link: "/my-research/generate",
          parentId: "my-research",
        },
        {
          id: "my-research-list",
          label: "My Research",
          link: "/my-research",
          parentId: "my-research",
        },
        {
          id: "my-research-scorecard",
          label: "Scorecard",
          link: "/my-research/scorecard",
          parentId: "my-research",
        },
      ],
    },
    {
      id: "predictions",
      label: "AI Predictions",
      icon: "ri-line-chart-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsPredictions(!isPredictions);
        setIscurrentState("Predictions");
        updateIconSidebar(e);
      },
      stateVariables: isPredictions,
      subItems: [
        {
          id: "predictions-dashboard",
          label: "Dashboard",
          link: "/predictions-dashboard",
          parentId: "predictions",
        },
        {
          id: "predictions-list",
          label: "View Predictions",
          link: "/predictions-list",
          parentId: "predictions",
        },
        {
          id: "predictions-manual-entry",
          label: "Manual Entry",
          link: "/predictions-manual-entry",
          parentId: "predictions",
        },
        {
          id: "predictions-api-keys",
          label: "API Key Management",
          link: "/predictions-api-keys",
          parentId: "predictions",
        },
      ],
    },
    // Current Report Menu Group
    {
      id: "report",
      label: "Current Report",
      icon: "ri-file-chart-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsReport(!isReport);
        setIscurrentState("Report");
        updateIconSidebar(e);
      },
      stateVariables: isReport,
      subItems: [
        {
          id: "report-btc-1d",
          label: "BTC Report - 1d",
          link: todaysBtcReportLink,
          parentId: "report",
        },
        {
          id: "report-eth-1d",
          label: "ETH Report - 1d",
          link: todaysEthReportLink,
          parentId: "report",
        },
        {
          id: "report-paxg-1d",
          label: "PAXG Report - 1d",
          link: todaysPaxgReportLink,
          parentId: "report",
        },
        {
          id: "report-xrp-1d",
          label: "XRP Report - 1d",
          link: todaysXrpReportLink,
          parentId: "report",
        },
        {
          id: "report-spy-1d",
          label: "SPY Report - 1d",
          link: todaysSpyReportLink,
          parentId: "report",
        },
        {
          id: "report-tqqq-1d",
          label: "TQQQ Report - 1d",
          link: todaysTqqqReportLink,
          parentId: "report",
        },
        {
          id: "report-ugl-1d",
          label: "UGL Report - 1d",
          link: todaysUglReportLink,
          parentId: "report",
        },
        {
          id: "report-soxl-1d",
          label: "SOXL Report - 1d",
          link: todaysSoxl1dReportLink,
          parentId: "report",
        },
        {
          id: "report-fas-1d",
          label: "FAS Report - 1d",
          link: todaysFas1dReportLink,
          parentId: "report",
        },
        {
          id: "report-tmf-1d",
          label: "TMF Report - 1d",
          link: todaysTmf1dReportLink,
          parentId: "report",
        },
        {
          id: "report-labu-1d",
          label: "LABU Report - 1d",
          link: todaysLabu1dReportLink,
          parentId: "report",
        },
        {
          id: "report-uco-1d",
          label: "UCO Report - 1d",
          link: todaysUco1dReportLink,
          parentId: "report",
        },
        {
          id: "report-fts-1d",
          label: "FTS Report - 1d",
          link: todaysFts1dReportLink,
          parentId: "report",
        },
        {
          id: "report-ibit-1d",
          label: "IBIT Report - 1d",
          link: todaysIbit1dReportLink,
          parentId: "report",
        },
        {
          id: "report-ionq-1d",
          label: "IONQ Report - 1d",
          link: todaysIonq1dReportLink,
          parentId: "report",
        },
        {
          id: "report-ceg-1d",
          label: "CEG Report - 1d",
          link: todaysCeg1dReportLink,
          parentId: "report",
        },
        {
          id: "report-qbts-1d",
          label: "QBTS Report - 1d",
          link: todaysQbts1dReportLink,
          parentId: "report",
        },
        {
          id: "report-mp-1d",
          label: "MP Report - 1d",
          link: todaysMp1dReportLink,
          parentId: "report",
        },
        {
          id: "report-goog-1d",
          label: "GOOG Report - 1d",
          link: todaysGoog1dReportLink,
          parentId: "report",
        },
        {
          id: "report-tsla-1d",
          label: "TSLA Report - 1d",
          link: todaysTsla1dReportLink,
          parentId: "report",
        },
        {
          id: "report-amzn-1d",
          label: "AMZN Report - 1d",
          link: todaysAmzn1dReportLink,
          parentId: "report",
        },
        {
          id: "report-aapl-1d",
          label: "AAPL Report - 1d",
          link: todaysAapl1dReportLink,
          parentId: "report",
        },
        {
          id: "report-msft-1d",
          label: "MSFT Report - 1d",
          link: todaysMsft1dReportLink,
          parentId: "report",
        },
        {
          id: "report-nvda-1d",
          label: "NVDA Report - 1d",
          link: todaysNvda1dReportLink,
          parentId: "report",
        },
        {
          id: "report-meta-1d",
          label: "META Report - 1d",
          link: todaysMeta1dReportLink,
          parentId: "report",
        },
        {
          id: "report-wsp-1d",
          label: "WSP.TO Report - 1d",
          link: todaysWsp1dReportLink,
          parentId: "report",
        },
        {
          id: "report-toggle-panel",
          label: "Show Options Panel",
          link: "#",
          parentId: "report",
          click: function (e) {
            e.preventDefault();
            e.stopPropagation();
            toggleReportPanel();
          }
        },
      ],
    },
    // Previous Report Menu Group
    {
      id: "previous-report",
      label: "Previous Report",
      icon: "ri-file-history-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsPreviousReport(!isPreviousReport);
        setIscurrentState("PreviousReport");
        updateIconSidebar(e);
      },
      stateVariables: isPreviousReport,
      subItems: [
        {
          id: "previous-report-btc-1d",
          label: "BTC Report - 1d",
          link: previousBtcReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-eth-1d",
          label: "ETH Report - 1d",
          link: previousEthReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-paxg-1d",
          label: "PAXG Report - 1d",
          link: previousPaxgReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-xrp-1d",
          label: "XRP Report - 1d",
          link: previousXrpReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-spy-1d",
          label: "SPY Report - 1d",
          link: previousSpyReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-tqqq-1d",
          label: "TQQQ Report - 1d",
          link: previousTqqqReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-ugl-1d",
          label: "UGL Report - 1d",
          link: previousUglReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-soxl-1d",
          label: "SOXL Report - 1d",
          link: previousSoxl1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-fas-1d",
          label: "FAS Report - 1d",
          link: previousFas1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-tmf-1d",
          label: "TMF Report - 1d",
          link: previousTmf1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-labu-1d",
          label: "LABU Report - 1d",
          link: previousLabu1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-uco-1d",
          label: "UCO Report - 1d",
          link: previousUco1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-fts-1d",
          label: "FTS Report - 1d",
          link: previousFts1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-ibit-1d",
          label: "IBIT Report - 1d",
          link: previousIbit1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-ionq-1d",
          label: "IONQ Report - 1d",
          link: previousIonq1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-ceg-1d",
          label: "CEG Report - 1d",
          link: previousCeg1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-qbts-1d",
          label: "QBTS Report - 1d",
          link: previousQbts1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-mp-1d",
          label: "MP Report - 1d",
          link: previousMp1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-goog-1d",
          label: "GOOG Report - 1d",
          link: previousGoog1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-tsla-1d",
          label: "TSLA Report - 1d",
          link: previousTsla1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-amzn-1d",
          label: "AMZN Report - 1d",
          link: previousAmzn1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-aapl-1d",
          label: "AAPL Report - 1d",
          link: previousAapl1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-msft-1d",
          label: "MSFT Report - 1d",
          link: previousMsft1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-nvda-1d",
          label: "NVDA Report - 1d",
          link: previousNvda1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-meta-1d",
          label: "META Report - 1d",
          link: previousMeta1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-wsp-1d",
          label: "WSP.TO Report - 1d",
          link: previousWsp1dReportLink,
          parentId: "previous-report",
        },
        {
          id: "previous-report-toggle-panel",
          label: "Show Options Panel",
          link: "#",
          parentId: "previous-report",
          click: function (e) {
            e.preventDefault();
            e.stopPropagation();
            toggleReportPanel();
          }
        },
      ],
    },
    // Prediction History (Mode 2) - mirrors Current Report structure with last 30 days default
    {
      id: "prediction-history",
      label: "Prediction History",
      icon: "ri-history-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsPredictionHistory(!isPredictionHistory);
        setIscurrentState("PredictionHistory");
        updateIconSidebar(e);
      },
      stateVariables: isPredictionHistory,
      subItems: [
        { id: "history-btc-1d", label: "BTC Report - 1d", link: makeMode2DailyLink('BTC'), parentId: "prediction-history" },
        { id: "history-eth-1d", label: "ETH Report - 1d", link: makeMode2DailyLink('ETH'), parentId: "prediction-history" },
        { id: "history-paxg-1d", label: "PAXG Report - 1d", link: makeMode2DailyLink('PAXG'), parentId: "prediction-history" },
        { id: "history-xrp-1d", label: "XRP Report - 1d", link: makeMode2DailyLink('XRP'), parentId: "prediction-history" },
        { id: "history-spy-1d", label: "SPY Report - 1d", link: makeMode2DailyLink('SPY'), parentId: "prediction-history" },
        { id: "history-tqqq-1d", label: "TQQQ Report - 1d", link: makeMode2DailyLink('TQQQ'), parentId: "prediction-history" },
        { id: "history-ugl-1d", label: "UGL Report - 1d", link: makeMode2DailyLink('UGL'), parentId: "prediction-history" },
        { id: "history-soxl-1d", label: "SOXL Report - 1d", link: makeMode2DailyLink('SOXL'), parentId: "prediction-history" },
        { id: "history-fas-1d", label: "FAS Report - 1d", link: makeMode2DailyLink('FAS'), parentId: "prediction-history" },
        { id: "history-tmf-1d", label: "TMF Report - 1d", link: makeMode2DailyLink('TMF'), parentId: "prediction-history" },
        { id: "history-labu-1d", label: "LABU Report - 1d", link: makeMode2DailyLink('LABU'), parentId: "prediction-history" },
        { id: "history-uco-1d", label: "UCO Report - 1d", link: makeMode2DailyLink('UCO'), parentId: "prediction-history" },
        { id: "history-fts-1d", label: "FTS Report - 1d", link: makeMode2DailyLink('FTS'), parentId: "prediction-history" },
        { id: "history-ibit-1d", label: "IBIT Report - 1d", link: makeMode2DailyLink('IBIT'), parentId: "prediction-history" },
        { id: "history-ionq-1d", label: "IONQ Report - 1d", link: makeMode2DailyLink('IONQ'), parentId: "prediction-history" },
        { id: "history-ceg-1d", label: "CEG Report - 1d", link: makeMode2DailyLink('CEG'), parentId: "prediction-history" },
        { id: "history-qbts-1d", label: "QBTS Report - 1d", link: makeMode2DailyLink('QBTS'), parentId: "prediction-history" },
        { id: "history-mp-1d", label: "MP Report - 1d", link: makeMode2DailyLink('MP'), parentId: "prediction-history" },
        { id: "history-goog-1d", label: "GOOG Report - 1d", link: makeMode2DailyLink('GOOG'), parentId: "prediction-history" },
        { id: "history-tsla-1d", label: "TSLA Report - 1d", link: makeMode2DailyLink('TSLA'), parentId: "prediction-history" },
        { id: "history-amzn-1d", label: "AMZN Report - 1d", link: makeMode2DailyLink('AMZN'), parentId: "prediction-history" },
        { id: "history-aapl-1d", label: "AAPL Report - 1d", link: makeMode2DailyLink('AAPL'), parentId: "prediction-history" },
        { id: "history-msft-1d", label: "MSFT Report - 1d", link: makeMode2DailyLink('MSFT'), parentId: "prediction-history" },
        { id: "history-nvda-1d", label: "NVDA Report - 1d", link: makeMode2DailyLink('NVDA'), parentId: "prediction-history" },
        { id: "history-meta-1d", label: "META Report - 1d", link: makeMode2DailyLink('META'), parentId: "prediction-history" },
        {
          id: "history-toggle-panel",
          label: "Show Options Panel",
          link: "#",
          parentId: "prediction-history",
          click: function (e) {
            e.preventDefault();
            e.stopPropagation();
            toggleReportPanel();
          }
        },
      ],
    },
    // Score Report (Mode 3) - mirrors Current Report structure with last 30 days default
    {
      id: "score-report",
      label: "Score Report",
      icon: "ri-bar-chart-2-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsScoreReport(!isScoreReport);
        setIscurrentState("ScoreReport");
        updateIconSidebar(e);
      },
      stateVariables: isScoreReport,
      subItems: [
        { id: "score-btc-1d", label: "BTC Report - 1d", link: makeMode3DailyLink('BTC'), parentId: "score-report" },
        { id: "score-eth-1d", label: "ETH Report - 1d", link: makeMode3DailyLink('ETH'), parentId: "score-report" },
        { id: "score-paxg-1d", label: "PAXG Report - 1d", link: makeMode3DailyLink('PAXG'), parentId: "score-report" },
        { id: "score-xrp-1d", label: "XRP Report - 1d", link: makeMode3DailyLink('XRP'), parentId: "score-report" },
        { id: "score-spy-1d", label: "SPY Report - 1d", link: makeMode3DailyLink('SPY'), parentId: "score-report" },
        { id: "score-tqqq-1d", label: "TQQQ Report - 1d", link: makeMode3DailyLink('TQQQ'), parentId: "score-report" },
        { id: "score-ugl-1d", label: "UGL Report - 1d", link: makeMode3DailyLink('UGL'), parentId: "score-report" },
        { id: "score-soxl-1d", label: "SOXL Report - 1d", link: makeMode3DailyLink('SOXL'), parentId: "score-report" },
        { id: "score-fas-1d", label: "FAS Report - 1d", link: makeMode3DailyLink('FAS'), parentId: "score-report" },
        { id: "score-tmf-1d", label: "TMF Report - 1d", link: makeMode3DailyLink('TMF'), parentId: "score-report" },
        { id: "score-labu-1d", label: "LABU Report - 1d", link: makeMode3DailyLink('LABU'), parentId: "score-report" },
        { id: "score-uco-1d", label: "UCO Report - 1d", link: makeMode3DailyLink('UCO'), parentId: "score-report" },
        { id: "score-fts-1d", label: "FTS Report - 1d", link: makeMode3DailyLink('FTS'), parentId: "score-report" },
        { id: "score-ibit-1d", label: "IBIT Report - 1d", link: makeMode3DailyLink('IBIT'), parentId: "score-report" },
        { id: "score-ionq-1d", label: "IONQ Report - 1d", link: makeMode3DailyLink('IONQ'), parentId: "score-report" },
        { id: "score-ceg-1d", label: "CEG Report - 1d", link: makeMode3DailyLink('CEG'), parentId: "score-report" },
        { id: "score-qbts-1d", label: "QBTS Report - 1d", link: makeMode3DailyLink('QBTS'), parentId: "score-report" },
        { id: "score-mp-1d", label: "MP Report - 1d", link: makeMode3DailyLink('MP'), parentId: "score-report" },
        { id: "score-goog-1d", label: "GOOG Report - 1d", link: makeMode3DailyLink('GOOG'), parentId: "score-report" },
        { id: "score-tsla-1d", label: "TSLA Report - 1d", link: makeMode3DailyLink('TSLA'), parentId: "score-report" },
        { id: "score-amzn-1d", label: "AMZN Report - 1d", link: makeMode3DailyLink('AMZN'), parentId: "score-report" },
        { id: "score-aapl-1d", label: "AAPL Report - 1d", link: makeMode3DailyLink('AAPL'), parentId: "score-report" },
        { id: "score-msft-1d", label: "MSFT Report - 1d", link: makeMode3DailyLink('MSFT'), parentId: "score-report" },
        { id: "score-nvda-1d", label: "NVDA Report - 1d", link: makeMode3DailyLink('NVDA'), parentId: "score-report" },
        { id: "score-meta-1d", label: "META Report - 1d", link: makeMode3DailyLink('META'), parentId: "score-report" },
        {
          id: "score-toggle-panel",
          label: "Show Options Panel",
          link: "#",
          parentId: "score-report",
          click: function (e) {
            e.preventDefault();
            e.stopPropagation();
            toggleReportPanel();
          }
        },
      ],
    },
    {
      label: "pages",
      isHeader: true,
    },
    {
      id: "authentication",
      label: "Authentication",
      icon: "ri-account-circle-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsAuth(!isAuth);
        setIscurrentState("Auth");
        updateIconSidebar(e);
      },
      stateVariables: isAuth,
      subItems: [
        {
          id: "signIn",
          label: "Sign In",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsSignIn(!isSignIn);
          },
          parentId: "authentication",
          stateVariables: isSignIn,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-signin-basic" },
            { id: 2, label: "Cover", link: "/auth-signin-cover" },
          ],
        },
        {
          id: "signUp",
          label: "Sign Up",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsSignUp(!isSignUp);
          },
          parentId: "authentication",
          stateVariables: isSignUp,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-signup-basic" },
            { id: 2, label: "Cover", link: "/auth-signup-cover" },
          ],
        },
        {
          id: "passwordReset",
          label: "Password Reset",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsPasswordReset(!isPasswordReset);
          },
          parentId: "authentication",
          stateVariables: isPasswordReset,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-pass-reset-basic" },
            { id: 2, label: "Cover", link: "/auth-pass-reset-cover" },
          ],
        },
        {
          id: "passwordCreate",
          label: "Password Create",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsPasswordCreate(!isPasswordCreate);
          },
          parentId: "authentication",
          stateVariables: isPasswordCreate,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-pass-change-basic" },
            { id: 2, label: "Cover", link: "/auth-pass-change-cover" },
          ],
        },
        {
          id: "lockScreen",
          label: "Lock Screen",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsLockScreen(!isLockScreen);
          },
          parentId: "authentication",
          stateVariables: isLockScreen,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-lockscreen-basic" },
            { id: 2, label: "Cover", link: "/auth-lockscreen-cover" },
          ],
        },
        {
          id: "logout",
          label: "Logout",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsLogout(!isLogout);
          },
          parentId: "authentication",
          stateVariables: isLogout,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-logout-basic" },
            { id: 2, label: "Cover", link: "/auth-logout-cover" },
          ],
        },
        {
          id: "successMessage",
          label: "Success Message",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsSuccessMessage(!isSuccessMessage);
          },
          parentId: "authentication",
          stateVariables: isSuccessMessage,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-success-msg-basic" },
            { id: 2, label: "Cover", link: "/auth-success-msg-cover" },
          ],
        },
        {
          id: "twoStepVerification",
          label: "Two Step Verification",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsVerification(!isVerification);
          },
          parentId: "authentication",
          stateVariables: isVerification,
          childItems: [
            { id: 1, label: "Basic", link: "/auth-twostep-basic" },
            { id: 2, label: "Cover", link: "/auth-twostep-cover" },
          ],
        },
        {
          id: "errors",
          label: "Errors",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsError(!isError);
          },
          parentId: "authentication",
          stateVariables: isError,
          childItems: [
            { id: 1, label: "404 Basic", link: "/auth-404-basic" },
            { id: 2, label: "404 Cover", link: "/auth-404-cover" },
            { id: 3, label: "404 Alt", link: "/auth-404-alt" },
            { id: 4, label: "500", link: "/auth-500" },
            { id: 5, label: "Offline Page", link: "/auth-offline" },
          ],
        },
      ],
    },
    {
      id: "pages",
      label: "Pages",
      icon: "ri-pages-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsPages(!isPages);
        setIscurrentState("Pages");
        updateIconSidebar(e);
      },
      stateVariables: isPages,
      subItems: [
        {
          id: "starter",
          label: "Starter",
          link: "/pages-starter",
          parentId: "pages",
        },
        {
          id: "profile",
          label: "Profile",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsProfile(!isProfile);
          },
          parentId: "pages",
          stateVariables: isProfile,
          childItems: [
            {
              id: 1,
              label: "Simple Page",
              link: "/pages-profile",
              parentId: "pages",
            },
            {
              id: 2,
              label: "Settings",
              link: "/pages-profile-settings",
              parentId: "pages",
            },
          ],
        },
        { id: "team", label: "Team", link: "/pages-team", parentId: "pages" },
        {
          id: "timeline",
          label: "Timeline",
          link: "/pages-timeline",
          parentId: "pages",
        },
        { id: "faqs", label: "FAQs", link: "/pages-faqs", parentId: "pages" },
        {
          id: "pricing",
          label: "Pricing",
          link: "/pages-pricing",
          parentId: "pages",
        },
        {
          id: "gallery",
          label: "Gallery",
          link: "/pages-gallery",
          parentId: "pages",
        },
        {
          id: "maintenance",
          label: "Maintenance",
          link: "/pages-maintenance",
          parentId: "pages",
        },
        {
          id: "comingSoon",
          label: "Coming Soon",
          link: "/pages-coming-soon",
          parentId: "pages",
        },
        {
          id: "sitemap",
          label: "Sitemap",
          link: "/pages-sitemap",
          parentId: "pages",
        },
        {
          id: "searchResults",
          label: "Search Results",
          link: "/pages-search-results",
          parentId: "pages",
        },
        {
          id: "PrivecyPolicy",
          label: "Privacy Policy",
          link: "/pages-privacy-policy",
          parentId: "pages",
        },
        {
          id: "TermsCondition",
          label: "Terms Condition",
          link: "/pages-terms-condition",
          parentId: "pages",
        },
        {
          id: "blogs",
          label: "Blogs",
          link: "/#",
          isChildItem: true,
          badgeColor: "success", badgeName: "New",
          click: function (e) {
            e.preventDefault();
            setIsBlog(!isBlog);
          },
          parentId: "pages",
          stateVariables: isBlog,
          childItems: [
            { id: 1, label: "List View", link: "/pages-blog-list", parentId: "pages" },
            { id: 2, label: "Grid View", link: "/pages-blog-grid", parentId: "pages" },
            { id: 3, label: "Overview", link: "/pages-blog-overview", parentId: "pages" },
          ]
        }
      ],
    },
    {
      id: "landing",
      label: "Landing",
      icon: "ri-rocket-line",
      link: "/#",
      stateVariables: isLanding,
      click: function (e) {
        e.preventDefault();
        setIsLanding(!isLanding);
        setIscurrentState("Landing");
        updateIconSidebar(e);
      },
      subItems: [
        {
          id: "onePage",
          label: "One Page",
          link: "/landing",
          parentId: "landing",
        },
        {
          id: "nftLanding",
          label: "NFT Landing",
          link: "/nft-landing",
          parentId: "landing",
        },
        {
          id: "jobLanding",
          label: "Job",
          link: "/job-landing",
          parentId: "landing",
          // badgeColor: "success", badgeName: "New"
        },
      ],
    },
    {
      label: "Components",
      isHeader: true,
    },
    {
      id: "baseUi",
      label: "Base UI",
      icon: "ri-pencil-ruler-2-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsBaseUi(!isBaseUi);
        setIscurrentState("BaseUi");
        updateIconSidebar(e);
      },
      stateVariables: isBaseUi,
      subItems: [
        {
          id: "alerts",
          label: "Alerts",
          link: "/ui-alerts",
          parentId: "baseUi",
        },
        {
          id: "badges",
          label: "Badges",
          link: "/ui-badges",
          parentId: "baseUi",
        },
        {
          id: "buttons",
          label: "Buttons",
          link: "/ui-buttons",
          parentId: "baseUi",
        },
        {
          id: "colors",
          label: "Colors",
          link: "/ui-colors",
          parentId: "baseUi",
        },
        { id: "cards", label: "Cards", link: "/ui-cards", parentId: "baseUi" },
        {
          id: "carousel",
          label: "Carousel",
          link: "/ui-carousel",
          parentId: "baseUi",
        },
        {
          id: "dropdowns",
          label: "Dropdowns",
          link: "/ui-dropdowns",
          parentId: "baseUi",
        },
        { id: "grid", label: "Grid", link: "/ui-grid", parentId: "baseUi" },
        {
          id: "images",
          label: "Images",
          link: "/ui-images",
          parentId: "baseUi",
        },
        { id: "tabs", label: "Tabs", link: "/ui-tabs", parentId: "baseUi" },
        {
          id: "accordions",
          label: "Accordion & Collapse",
          link: "/ui-accordions",
          parentId: "baseUi",
        },
        {
          id: "modals",
          label: "Modals",
          link: "/ui-modals",
          parentId: "baseUi",
        },
        {
          id: "offcanvas",
          label: "Offcanvas",
          link: "/ui-offcanvas",
          parentId: "baseUi",
        },
        {
          id: "placeholders",
          label: "Placeholders",
          link: "/ui-placeholders",
          parentId: "baseUi",
        },
        {
          id: "progress",
          label: "Progress",
          link: "/ui-progress",
          parentId: "baseUi",
        },
        {
          id: "notifications",
          label: "Notifications",
          link: "/ui-notifications",
          parentId: "baseUi",
        },
        {
          id: "media",
          label: "Media object",
          link: "/ui-media",
          parentId: "baseUi",
        },
        {
          id: "embedvideo",
          label: "Embed Video",
          link: "/ui-embed-video",
          parentId: "baseUi",
        },
        {
          id: "typography",
          label: "Typography",
          link: "/ui-typography",
          parentId: "baseUi",
        },
        { id: "lists", label: "Lists", link: "/ui-lists", parentId: "baseUi" },
        {
          id: "links",
          label: "Links",
          link: "/ui-links",
          parentId: "baseUi",
          badgeColor: "success",
          badgeName: "New",
        },
        {
          id: "general",
          label: "General",
          link: "/ui-general",
          parentId: "baseUi",
        },
        {
          id: "ribbons",
          label: "Ribbons",
          link: "/ui-ribbons",
          parentId: "baseUi",
        },
        {
          id: "utilities",
          label: "Utilities",
          link: "/ui-utilities",
          parentId: "baseUi",
        },
      ],
    },
    {
      id: "advanceUi",
      label: "Advance UI",
      icon: "ri-stack-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsAdvanceUi(!isAdvanceUi);
        setIscurrentState("AdvanceUi");
        updateIconSidebar(e);
      },
      stateVariables: isAdvanceUi,
      subItems: [
        {
          id: "nestablelist",
          label: "Nestable List",
          link: "/advance-ui-nestable",
          parentId: "advanceUi",
        },
        {
          id: "scrollbar",
          label: "Scrollbar",
          link: "/advance-ui-scrollbar",
          parentId: "advanceUi",
        },
        {
          id: "animation",
          label: "Animation",
          link: "/advance-ui-animation",
          parentId: "advanceUi",
        },
        {
          id: "swiperslider",
          label: "Swiper Slider",
          link: "/advance-ui-swiper",
          parentId: "advanceUi",
        },
        {
          id: "ratings",
          label: "Ratings",
          link: "/advance-ui-ratings",
          parentId: "advanceUi",
        },
        {
          id: "highlight",
          label: "Highlight",
          link: "/advance-ui-highlight",
          parentId: "advanceUi",
        },
      ],
    },
    {
      id: "widgets",
      label: "Widgets",
      icon: "ri-honour-line",
      link: "/widgets",
      click: function (e) {
        e.preventDefault();
        setIscurrentState("Widgets");
      },
    },
    {
      id: "forms",
      label: "Forms",
      icon: "ri-file-list-3-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsForms(!isForms);
        setIscurrentState("Forms");
        updateIconSidebar(e);
      },
      stateVariables: isForms,
      subItems: [
        {
          id: "basicelements",
          label: "Basic Elements",
          link: "/forms-elements",
          parentId: "forms",
        },
        {
          id: "formselect",
          label: "Form Select",
          link: "/forms-select",
          parentId: "forms",
        },
        {
          id: "checkboxsradios",
          label: "Checkboxs & Radios",
          link: "/forms-checkboxes-radios",
          parentId: "forms",
        },
        {
          id: "pickers",
          label: "Pickers",
          link: "/forms-pickers",
          parentId: "forms",
        },
        {
          id: "inputmasks",
          label: "Input Masks",
          link: "/forms-masks",
          parentId: "forms",
        },
        {
          id: "advanced",
          label: "Advanced",
          link: "/forms-advanced",
          parentId: "forms",
        },
        {
          id: "rangeslider",
          label: "Range Slider",
          link: "/forms-range-sliders",
          parentId: "forms",
        },
        {
          id: "validation",
          label: "Validation",
          link: "/forms-validation",
          parentId: "forms",
        },
        {
          id: "wizard",
          label: "Wizard",
          link: "/forms-wizard",
          parentId: "forms",
        },
        {
          id: "editors",
          label: "Editors",
          link: "/forms-editors",
          parentId: "forms",
        },
        {
          id: "fileuploads",
          label: "File Uploads",
          link: "/forms-file-uploads",
          parentId: "forms",
        },
        {
          id: "formlayouts",
          label: "Form Layouts",
          link: "/forms-layouts",
          parentId: "forms",
        },
        {
          id: "select2",
          label: "Select2",
          link: "/forms-select2",
          parentId: "forms",
        },
      ],
    },
    {
      id: "tables",
      label: "Tables",
      icon: "ri-layout-grid-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsTables(!isTables);
        setIscurrentState("Tables");
        updateIconSidebar(e);
      },
      stateVariables: isTables,
      subItems: [
        {
          id: "basictables",
          label: "Basic Tables",
          link: "/tables-basic",
          parentId: "tables",
        },
        {
          id: "listjs",
          label: "List Js",
          link: "/tables-listjs",
          parentId: "tables",
        },
        {
          id: "reactdatatables",
          label: "React Datatables",
          link: "/tables-react",
          parentId: "tables",
        },
      ],
    },
    {
      id: "charts",
      label: "Charts",
      icon: "ri-pie-chart-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsCharts(!isCharts);
        setIscurrentState("Charts");
        updateIconSidebar(e);
      },
      stateVariables: isCharts,
      subItems: [
        {
          id: "apexcharts",
          label: "Apexcharts",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsApex(!isApex);
          },
          stateVariables: isApex,
          childItems: [
            { id: 1, label: "Line", link: "/charts-apex-line" },
            { id: 2, label: "Area", link: "/charts-apex-area" },
            { id: 3, label: "Column", link: "/charts-apex-column" },
            { id: 4, label: "Bar", link: "/charts-apex-bar" },
            { id: 5, label: "Mixed", link: "/charts-apex-mixed" },
            { id: 6, label: "Timeline", link: "/charts-apex-timeline" },
            {
              id: 7,
              label: "Range Area",
              link: "/charts-apex-range-area",
              parentId: "apexcharts",
              badgeColor: "success",
              badgeName: "New",
            },
            {
              id: 8,
              label: "Funnel",
              link: "/charts-apex-funnel",
              parentId: "apexcharts",
              badgeColor: "success",
              badgeName: "New",
            },
            { id: 9, label: "Candlstick", link: "/charts-apex-candlestick" },
            { id: 10, label: "Boxplot", link: "/charts-apex-boxplot" },
            { id: 11, label: "Bubble", link: "/charts-apex-bubble" },
            { id: 12, label: "Scatter", link: "/charts-apex-scatter" },
            { id: 13, label: "Heatmap", link: "/charts-apex-heatmap" },
            { id: 14, label: "Treemap", link: "/charts-apex-treemap" },
            { id: 15, label: "Pie", link: "/charts-apex-pie" },
            { id: 16, label: "Radialbar", link: "/charts-apex-radialbar" },
            { id: 17, label: "Radar", link: "/charts-apex-radar" },
            { id: 18, label: "Polar Area", link: "/charts-apex-polar" },
            { id: 19, label: "Slope", link: "/charts-apex-slope", parentId: "charts", badgeColor: "success", badgeName: "New" },
          ],
        },
        {
          id: "chartjs",
          label: "Chartjs",
          link: "/charts-chartjs",
          parentId: "charts",
        },
        {
          id: "echarts",
          label: "Echarts",
          link: "/charts-echarts",
          parentId: "charts",
        },
      ],
    },
    {
      id: "icons",
      label: "Icons",
      icon: "ri-compasses-2-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsIcons(!isIcons);
        setIscurrentState("Icons");
        updateIconSidebar(e);
      },
      stateVariables: isIcons,
      subItems: [
        {
          id: "remix",
          label: "Remix",
          link: "/icons-remix",
          parentId: "icons",
        },
        {
          id: "boxicons",
          label: "Boxicons",
          link: "/icons-boxicons",
          parentId: "icons",
        },
        {
          id: "materialdesign",
          label: "Material Design",
          link: "/icons-materialdesign",
          parentId: "icons",
        },
        {
          id: "lineawesome",
          label: "Line Awesome",
          link: "/icons-lineawesome",
          parentId: "icons",
        },
        {
          id: "feather",
          label: "Feather",
          link: "/icons-feather",
          parentId: "icons",
        },
        {
          id: "crypto",
          label: "Crypto SVG",
          link: "/icons-crypto",
          parentId: "icons",
        },
      ],
    },
    {
      id: "maps",
      label: "Maps",
      icon: "ri-map-pin-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsMaps(!isMaps);
        setIscurrentState("Maps");
        updateIconSidebar(e);
      },
      stateVariables: isMaps,
      subItems: [
        {
          id: "google",
          label: "Google",
          link: "/maps-google",
          parentId: "maps",
        },
      ],
    },
    {
      id: "multilevel",
      label: "Multi Level",
      icon: "ri-share-line",
      link: "/#",
      click: function (e) {
        e.preventDefault();
        setIsMultiLevel(!isMultiLevel);
        setIscurrentState("MuliLevel");
        updateIconSidebar(e);
      },
      stateVariables: isMultiLevel,
      subItems: [
        {
          id: "level1.1",
          label: "Level 1.1",
          link: "/#",
          parentId: "multilevel",
        },
        {
          id: "level1.2",
          label: "Level 1.2",
          link: "/#",
          isChildItem: true,
          click: function (e) {
            e.preventDefault();
            setIsLevel1(!isLevel1);
          },
          stateVariables: isLevel1,
          childItems: [
            { id: 1, label: "Level 2.1", link: "/#" },
            {
              id: "level2.2",
              label: "Level 2.2",
              link: "/#",
              isChildItem: true,
              click: function (e) {
                e.preventDefault();
                setIsLevel2(!isLevel2);
              },
              stateVariables: isLevel2,
              childItems: [
                { id: 1, label: "Level 3.1", link: "/#" },
                { id: 2, label: "Level 3.2", link: "/#" },
              ],
            },
          ],
        },
      ],
    },
  ];
  
  // Which entries this user may see, headers aside.
  const isVisibleEntry = (item) => {
    if (isAdmin) {
      // showAllMenus reveals the full template menu for debugging.
      if (showAllMenus) {
        return item.isVisible !== false;
      }
      return ['my-research','predictions','report','previous-report','prediction-history','score-report','system-admin'].includes(item.id);
    }

    // A member gets their own research and nothing else. The report menus
    // render system-generated forecasts, which are internal: built from
    // purchased market data, so showing them is display use of a licence this
    // project does not hold, and charging for them would be selling
    // predictions. canSeeSystemResearch is now "is an administrator" — no tier
    // opens these, so the check below only ever passes for staff.
    if (item.id === 'my-research') return true;
    return false;
  };

  const withHeaders = allMenuItems.filter((item) => item.isHeader || isVisibleEntry(item));

  // A section header only earns its place if something under it survived. The
  // template's "Pages" and "Components" headings were returned unconditionally,
  // so a non-admin saw those captions with nothing beneath them.
  const filteredMenuItems = withHeaders.filter((item, index) => {
    if (!item.isHeader) return true;
    const next = withHeaders[index + 1];
    return !!next && !next.isHeader;
  });

  const researchIndex = filteredMenuItems.findIndex(item => item.id === 'my-research');
  const insertionIndex = researchIndex < 0 ? filteredMenuItems.length : researchIndex + 1;
  return [...filteredMenuItems.slice(0, insertionIndex), ...pluginMenu, ...filteredMenuItems.slice(insertionIndex)];
  }, [pluginMenu, isAdmin, isDashboard, isApps, isAuth, isPages, isBaseUi, isAdvanceUi, isForms, isTables, isCharts, isIcons, isMaps, isMultiLevel, isPredictions, isMyResearch, isReport, isPreviousReport, isSystemAdmin, showAllMenus, canSeeSystemResearch, history, iscurrentState, dailyDateMap, todayStr, primaryModelsParam, basicModelsParam]);
  
  return <React.Fragment>{menuItems}</React.Fragment>;
};
export default Navdata;
